import React, { Component } from "react";
import { Row, Col } from "react-bootstrap";
import gql from "graphql-tag";
import moment from "moment";
import CardLessDataTable from "../datatables/CardLessDataTableWithSearch";
import { ExpenseDashboardShimmer } from "./ExpenseShimmer";
import * as Cookies from "es-cookie";
import { scopeBranches, onBranchScopeChange } from "../../../utils/branchScope";
import {
  PieChart, Pie, Cell, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, AreaChart, Area,
} from "recharts";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import DateBlock from "../home_bck/DateBlock";

// ─── GraphQL Queries & Mutations ───────────────────────────────────────────
const GET_EXPENSES = gql`
  query ($filter: ExpenseFilterInput!) {
    getExpenses(filter: $filter) {
      id
      expense_date
      amount
      tax_amount
      discount_amount
      final_amount
      payment_status
      description
      reference_no
      expense_head_id
      expense_category_id
      expense_vendor_id
      head_name
      category_name
      vendor_name
      attachments
      approved_by_name
      payment_mode_id
    }
  }
`;

const GET_ACADEMIC_YEARS = gql`
  query getAcademicYears($id: Int) {
    getAcademicYears(id: $id) {
      id
      academic_year
    }
  }
`;

const GET_BRANCHES = gql`
  query getBranches($institution_id: Int!) {
    getBranches(institution_id: $institution_id) {
      id
      branch_name
      sections {
        id
        class_id
        section_name
      }
    }
  }
`;

const GET_ORGANISATION_PROFILE = gql`
  query($institution_id: Int!) {
    getOrganisationProfile(institution_id: $institution_id) {
      id
      organisation_name
      logo
      complete_address
    }
  }
`;

const APPROVE_EXPENSE = gql`
  mutation ($id: ID!, $status: String!) {
    approveExpense(id: $id, status: $status) {
      id
      payment_status
    }
  }
`;

// ─── Styling Constants ──────────────────────────────────────────────────────
const PIE_COLORS = ["#10b981", "#f97316", "#ef4444"];
const EXPENSE_COLORS = ["#6366f1", "#10b981", "#f97316", "#3b82f6", "#8b5cf6", "#f59e0b"];

const rupeeFmt = (v) => `₹${(v || 0).toLocaleString("en-IN")}`;

// ── Checkbox Dropdown Component ──────────────────────────────────────────────
class CheckboxDropdown extends React.Component {
  constructor(props) {
    super(props);
    this.state = { open: false };
    this.ref = React.createRef();
  }

  componentDidMount() { document.addEventListener("mousedown", this.handleOutside); }
  componentWillUnmount() { document.removeEventListener("mousedown", this.handleOutside); }

  handleOutside = (e) => {
    if (this.ref.current && !this.ref.current.contains(e.target))
      this.setState({ open: false });
  };

  toggle = (id) => {
    const { value, onChange } = this.props;
    const exists = value.includes(id);
    onChange(exists ? value.filter((v) => v !== id) : [...value, id]);
  };

  render() {
    const { options, value, placeholder, onChange } = this.props;
    const { open } = this.state;
    const label = value.length === 0
      ? placeholder
      : value.length === options.length
        ? `All (${options.length})`
        : value.length === 1
          ? options.find((o) => o.value === value[0])?.label || `${value.length} selected`
          : `${value.length} selected`;

    return (
      <div ref={this.ref} style={{ position: "relative" }}>
        <div onClick={() => this.setState({ open: !open })}
          style={{ borderRadius: 9, border: "1.5px solid #e5e7eb", padding: "8px 12px", fontSize: 13, color: value.length ? "#1f2937" : "#9ca3af", background: "#f9fafb", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", userSelect: "none" }}>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</span>
          <i className={`fal fa-chevron-${open ? "up" : "down"}`} style={{ fontSize: 11, color: "#9ca3af", flexShrink: 0, marginLeft: 6 }} />
        </div>

        {open && (
          <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, background: "#fff", border: "1.5px solid #e5e7eb", borderRadius: 10, boxShadow: "0 8px 24px rgba(0,0,0,0.10)", zIndex: 9999, maxHeight: 220, overflowY: "auto" }}>
            {options.length === 0 ? (
              <div style={{ padding: "10px 14px", fontSize: 12, color: "#9ca3af" }}>No options</div>
            ) : (
              <>
                <div onClick={() => onChange(value.length === options.length ? [] : options.map((o) => o.value))}
                  style={{ padding: "8px 12px", fontSize: 12, fontWeight: 700, color: "#6366f1", cursor: "pointer", borderBottom: "1px solid #f3f4f6", display: "flex", alignItems: "center", gap: 8 }}>
                  <input type="checkbox" readOnly checked={value.length === options.length} style={{ accentColor: "#6366f1" }} />
                  Select All
                </div>
                {options.map((o) => (
                  <div key={o.value} onClick={() => this.toggle(o.value)}
                    style={{ padding: "7px 12px", fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 8, background: value.includes(o.value) ? "#f5f3ff" : "transparent", borderBottom: "1px solid #f9fafb" }}>
                    <input type="checkbox" readOnly checked={value.includes(o.value)} style={{ accentColor: "#6366f1" }} />
                    <span style={{ color: value.includes(o.value) ? "#6d28d9" : "#374151", fontWeight: value.includes(o.value) ? 600 : 400 }}>{o.label}</span>
                  </div>
                ))}
              </>
            )}
          </div>
        )}
      </div>
    );
  }
}

// ── Recharts Tooltip Custom Renderers ────────────────────────────────────────
const PieTip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: "8px 14px", fontSize: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}>
      <strong>{payload[0].name}</strong>: {rupeeFmt(payload[0].value)}
    </div>
  );
};

const BarTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: "8px 14px", fontSize: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}>
      <div style={{ fontWeight: 700, marginBottom: 4 }}>{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} style={{ color: p.color }}>{p.name}: {rupeeFmt(p.value)}</div>
      ))}
    </div>
  );
};

// ── Main Dashboard Component ────────────────────────────────────────────────
class ExpenseDashboard extends Component {
  constructor(props) {
    super(props);
    const today = moment().format("YYYY-MM-DD");
    const defaultStart = today;

    this.state = {
      dateMode: "custom",
      // Configuration lists
      branches: [],
      orgProfile: {},

      // Dropdown selected filters
      branchSearch: [],
      headSearch: [],
      categorySearch: [],
      vendorSearch: [],
      statusSearch: [], // "Paid" | "pending" | "Rejected"
      fromDate: defaultStart,
      toDate: today,

      // Loaded operational data
      expenses: [],
      loading: true,

      // Navigation tab
      activeTab: "consolidated", // "consolidated" | "date_wise" | "vendor_wise" | "individual"
      expandedVendor: null,
    };
  }

  componentDidMount() {
    this.initMetadata();
    // Branch comes from the header (NavbarOne) — re-scope and refetch on change.
    this.offBranchScope = onBranchScopeChange(() => this.initMetadata());
  }

  componentWillUnmount() {
    if (this.offBranchScope) this.offBranchScope();
  }

  componentDidUpdate(prevProps) {
    const { configs } = this.props;
    const prevConfigs = prevProps.configs;

    const currentHeads = configs?.getExpenseHeads || [];
    const prevHeads = prevConfigs?.getExpenseHeads || [];

    if (currentHeads.length > 0 && prevHeads.length === 0 && this.state.headSearch.length === 0) {
      this.setState({
        headSearch: currentHeads.map(h => h.id),
        categorySearch: (configs?.getExpenseCategories || []).map(c => c.id),
        vendorSearch: [...(configs?.getExpenseVendors || []).map(v => v.id), null],
        statusSearch: ["Paid", "pending", "Rejected"]
      });
    }
  }

  handleDateBlockCallback = (startDate, endDate) => {
    this.setState({
      dateMode: "custom",
      fromDate: startDate.format("YYYY-MM-DD"),
      toDate: endDate.format("YYYY-MM-DD")
    }, this.fetchExpenses);
  };

  initMetadata = async () => {
    const { client, configs } = this.props;
    const instId = parseInt(Cookies.get("institutionid") || Cookies.get("institute_id") || "0");

    try {
      // 1. Fetch branches
      const branchRes = await client.query({
        query: GET_BRANCHES,
        variables: { institution_id: instId },
        fetchPolicy: "network-only"
      });
      // The header's branch selection IS the branch filter for this dashboard.
      const branches = scopeBranches(branchRes.data.getBranches || []);

      // 2. Fetch organization profile
      const orgRes = await client.query({
        query: GET_ORGANISATION_PROFILE,
        variables: { institution_id: instId },
        fetchPolicy: "network-only"
      });
      const orgProfile = orgRes.data.getOrganisationProfile || {};

      // Build default selection lists
      const headIds = (configs?.getExpenseHeads || []).map(h => h.id);
      const catIds = (configs?.getExpenseCategories || []).map(c => c.id);
      const venIds = [...(configs?.getExpenseVendors || []).map(v => v.id), null];
      const defaultStatuses = ["Paid", "pending", "Rejected"];

      this.setState({
        branches,
        orgProfile,
        branchSearch: branches.map(b => b.id),
        headSearch: headIds,
        categorySearch: catIds,
        vendorSearch: venIds,
        statusSearch: defaultStatuses,
      }, this.fetchExpenses);

    } catch (err) {
      console.error("Error initializing dashboard metadata:", err);
      this.setState({ loading: false });
    }
  };


  fetchExpenses = async () => {
    const { client } = this.props;
    const { dateMode, fromDate, toDate } = this.state;
    const instId = parseInt(Cookies.get("institutionid") || Cookies.get("institute_id") || "0");

    this.setState({ loading: true });
    try {
      const filter = { institute_id: instId };

      if (dateMode !== "overall") {
        if (fromDate && toDate && fromDate <= toDate) {
          filter.from_date = fromDate;
          filter.to_date = toDate;
        } else {
          this.setState({ loading: false });
          return;
        }
      }

      const { data } = await client.query({
        query: GET_EXPENSES,
        variables: { filter },
        fetchPolicy: "network-only"
      });

      this.setState({
        expenses: data.getExpenses || [],
        loading: false
      });
    } catch (err) {
      console.error("Error querying expenses:", err);
      this.setState({ loading: false });
    }
  };

  handleApprove = async (id, status) => {
    const { client } = this.props;
    const actionWord = status === "Paid" ? "approve" : "reject";
    if (!window.confirm(`Are you sure you want to ${actionWord} this expense record?`)) return;

    try {
      await client.mutate({
        mutation: APPROVE_EXPENSE,
        variables: { id, status }
      });
      this.fetchExpenses();
    } catch (err) {
      alert("Error updating expense status: " + err.message);
    }
  };

  getLogoDataUrl = async (logoFile) => {
    if (!logoFile) return "";
    const url = `https://support.rizee.in/files/${logoFile}`;
    try {
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) return url;
      const blob = await res.blob();
      return await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => resolve(url);
        reader.readAsDataURL(blob);
      });
    } catch {
      return url;
    }
  };

  // ─── Export & Print Capabilities ───────────────────────────────────────────
  printDateWiseReport = async (dateRows, customTotal, customTxnCount) => {
    const { orgProfile, dateMode, fromDate, toDate } = this.state;
    const dateRangeStr = dateMode === "overall" ? "Overall (All Time)" : `${fromDate} to ${toDate}`;
    const orgName = orgProfile.organisation_name || "";
    const logoFile = orgProfile.logo || "";
    const logoUrl = await this.getLogoDataUrl(logoFile);
    const orgAddress = orgProfile.complete_address || "";

    const rows = dateRows.map(r => {
      const subEntries = Object.entries(r.heads);
      return subEntries.map(([head, v], si) =>
        `<tr>
          ${si === 0 ? `<td rowspan="${subEntries.length}" style="font-weight:700;background:#eff6ff;color:#1d4ed8;vertical-align:top">${r.date}</td>` : ""}
          <td>${head}</td>
          <td style="text-align:center">${v.count}</td>
          <td style="text-align:right">₹${v.total.toLocaleString('en-IN')}</td>
        </tr>`
      ).join('');
    }).join('');

    const win = window.open('', '_blank', 'width=1100,height=700');
    win.document.write(`<html><head><title>Date Wise Report</title><style>
        *{margin:0;padding:0;box-sizing:border-box}
        body{font-family:Arial,sans-serif;padding:24px;color:#111}
        .header{display:flex;align-items:flex-start;gap:14px;border-bottom:2px solid #333;padding-bottom:12px;margin-bottom:16px}
        .header img{width:auto;height:60px;object-fit:contain;flex-shrink:0}
        .org-name{font-size:20px;font-weight:700;color:#111;margin-bottom:3px}
        .org-address{font-size:11px;color:#555;line-height:1.5;max-width:500px}
        .report-title{font-size:15px;font-weight:700;margin-bottom:4px}
        .report-sub{font-size:12px;color:#555;margin-bottom:16px}
        table{width:100%;border-collapse:collapse;font-size:12px}
        th{background:#f3f4f6;padding:7px 10px;text-align:left;font-size:10px;text-transform:uppercase;letter-spacing:.5px;border-bottom:2px solid #d1d5db;white-space:nowrap}
        td{padding:8px 10px;border-bottom:1px solid #f0f0f0;white-space:nowrap}
        tfoot td{font-weight:700;border-top:2px solid #d1d5db;background:#f8fafc}
        @media print{body{padding:10px}}
    </style></head><body>
        <div class="header">
            ${logoUrl ? `<img src="${logoUrl}" />` : ""}
            <div>
                <div class="org-name">${orgName}</div>
                <div class="org-address">${orgAddress}</div>
            </div>
        </div>
        <div class="report-title">Date Wise Expense Report</div>
        <div class="report-sub">${dateRangeStr}</div>
        <table>
            <thead><tr><th>Date</th><th>Expense Head</th><th style="text-align:center">Txn</th><th style="text-align:right">Amount</th></tr></thead>
            <tbody>${rows}</tbody>
            <tfoot><tr><td colspan="2">Grand Total</td><td style="text-align:center">${customTxnCount}</td><td style="text-align:right">₹${(customTotal || 0).toLocaleString('en-IN')}</td></tr></tfoot>
        </table>
        <script>
            ${logoUrl ? `var img=document.querySelector('img');if(img&&!img.complete){img.onload=function(){window.print();};img.onerror=function(){window.print()};}else{window.print();}` : `window.print();`}
        </script>
    </body></html>`);
    win.document.close();
  };

  printIndividualReport = async (rowsData, customTotal) => {
    const { orgProfile, dateMode, fromDate, toDate } = this.state;
    const dateRangeStr = dateMode === "overall" ? "Overall (All Time)" : `${fromDate} to ${toDate}`;
    const orgName = orgProfile.organisation_name || "";
    const logoFile = orgProfile.logo || "";
    const logoUrl = await this.getLogoDataUrl(logoFile);
    const orgAddress = orgProfile.complete_address || "";

    const rows = rowsData.map(row =>
      `<tr>
        <td>${row.date}</td>
        <td>${row.reference_no || '—'}</td>
        <td>${row.head_name}</td>
        <td>${row.category_name}</td>
        <td>${row.vendor_name || '—'}</td>
        <td>₹${(row.amount || 0).toLocaleString('en-IN')}</td>
        <td>₹${(row.tax_amount || 0).toLocaleString('en-IN')}</td>
        <td>₹${(row.discount_amount || 0).toLocaleString('en-IN')}</td>
        <td style="text-align:right">&#x20B9;${(row.final_amount || 0).toLocaleString('en-IN')}</td>
        <td>${row.payment_mode}</td>
        <td>${row.payment_status}</td>
      </tr>`
    ).join('');

    const win = window.open('', '_blank', 'width=1200,height=800');
    win.document.write(`<html><head><title>Individual Expense Report</title><style>
        *{margin:0;padding:0;box-sizing:border-box}
        body{font-family:Arial,sans-serif;padding:24px;color:#111}
        .header{display:flex;align-items:flex-start;gap:14px;border-bottom:2px solid #333;padding-bottom:12px;margin-bottom:16px}
        .header img{width:auto;height:60px;object-fit:contain;flex-shrink:0}
        .org-name{font-size:20px;font-weight:700;color:#111;margin-bottom:3px}
        .org-address{font-size:11px;color:#555;line-height:1.5;max-width:500px}
        .report-title{font-size:15px;font-weight:700;margin-bottom:4px}
        .report-sub{font-size:12px;color:#555;margin-bottom:16px}
        table{width:100%;border-collapse:collapse;font-size:11px}
        th{background:#f3f4f6;padding:7px 10px;text-align:left;font-size:9px;text-transform:uppercase;letter-spacing:.5px;border-bottom:2px solid #d1d5db;white-space:nowrap}
        td{padding:6px 10px;border-bottom:1px solid #f0f0f0;white-space:nowrap}
        tr:nth-child(even) td{background:#fafafa}
        tfoot td{font-weight:700;border-top:2px solid #d1d5db;background:#f8fafc}
        @media print{body{padding:10px}}
    </style></head><body>
        <div class="header">
            ${logoUrl ? `<img src="${logoUrl}" />` : ""}
            <div>
                <div class="org-name">${orgName}</div>
                <div class="org-address">${orgAddress}</div>
            </div>
        </div>
        <div class="report-title">Individual Expense Report</div>
        <div class="report-sub">${dateRangeStr} &nbsp;|&nbsp; ${rowsData.length} records</div>
        <table>
            <thead><tr><th>Date</th><th>Ref No</th><th>Head</th><th>Category</th><th>Vendor</th><th>Amt</th><th>Tax</th><th>Disc</th><th style="text-align:right">Final</th><th>Mode</th><th>Status</th></tr></thead>
            <tbody>${rows}</tbody>
            <tfoot><tr><td colspan="8">Grand Total</td><td style="text-align:right">&#x20B9;${(customTotal || 0).toLocaleString('en-IN')}</td><td colspan="2"></td></tr></tfoot>
        </table>
        <script>
            ${logoUrl ? `var img=document.querySelector('img');if(img&&!img.complete){img.onload=function(){window.print();};img.onerror=function(){window.print()};}else{window.print();}` : `window.print();`}
        </script>
    </body></html>`);
    win.document.close();
  };

  printVendorWiseReport = async (vendorsRows, customTotal, customTxnCount) => {
    const { orgProfile, dateMode, fromDate, toDate } = this.state;
    const orgName = orgProfile.organisation_name || "";
    const logoFile = orgProfile.logo || "";
    const logoUrl = await this.getLogoDataUrl(logoFile);
    const orgAddress = orgProfile.complete_address || "";
    const dateRangeStr = dateMode === "overall" ? "Overall (All Time)" : `${fromDate} to ${toDate}`;

    const rows = vendorsRows.map(r => {
      const subEntries = Object.entries(r.heads);
      return subEntries.map(([head, v], si) =>
        `<tr>
          ${si === 0 ? `
            <td rowspan="${subEntries.length}" style="font-weight:700;background:#eff6ff;color:#1d4ed8;vertical-align:top">
              <div style="font-weight:700;color:#111827">${r.vendor_name}</div>
              ${r.contact_person || r.contact_no ? `<div style="font-size:10px;color:#6b7280;margin-top:2px">${r.contact_person || '—'} (${r.contact_no || '—'})</div>` : ''}
              ${r.comments ? `<div style="font-size:10px;color:#6b7280;font-style:italic;margin-top:2px">${r.comments}</div>` : ''}
            </td>` : ""}
          <td>${head}</td>
          <td style="text-align:center">${v.count}</td>
          <td style="text-align:right">₹${v.total.toLocaleString('en-IN')}</td>
        </tr>`
      ).join('');
    }).join('');

    const win = window.open('', '_blank', 'width=1100,height=700');
    win.document.write(`<html><head><title>Vendor Wise Report</title><style>
        *{margin:0;padding:0;box-sizing:border-box}
        body{font-family:Arial,sans-serif;padding:24px;color:#111}
        .header{display:flex;align-items:flex-start;gap:14px;border-bottom:2px solid #333;padding-bottom:12px;margin-bottom:16px}
        .header img{width:auto;height:60px;object-fit:contain;flex-shrink:0}
        .org-name{font-size:20px;font-weight:700;color:#111;margin-bottom:3px}
        .org-address{font-size:11px;color:#555;line-height:1.5;max-width:500px}
        .report-title{font-size:15px;font-weight:700;margin-bottom:4px}
        .report-sub{font-size:12px;color:#555;margin-bottom:16px}
        table{width:100%;border-collapse:collapse;font-size:12px}
        th{padding:7px 10px;text-align:left;font-size:10px;text-transform:uppercase;letter-spacing:.5px;border-bottom:2px solid #d1d5db;white-space:nowrap}
        td{padding:8px 10px;border-bottom:1px solid #f0f0f0;vertical-align:top}
        tfoot td{font-weight:700;border-top:2px solid #d1d5db;}
        @media print{body{padding:10px}}
    </style></head><body>
        <div class="header">
            ${logoUrl ? `<img src="${logoUrl}" />` : ""}
            <div>
                <div class="org-name">${orgName}</div>
                <div class="org-address">${orgAddress}</div>
            </div>
        </div>
        <div class="report-title">Vendor Wise Expense Report</div>
        <div class="report-sub">${dateRangeStr}</div>
        <table>
            <thead><tr><th>Vendor Details</th><th>Expense Head</th><th style="text-align:center">Txns</th><th style="text-align:right">Amount</th></tr></thead>
            <tbody>${rows}</tbody>
            <tfoot><tr><td colspan="2">Grand Total</td><td style="text-align:center">${customTxnCount}</td><td style="text-align:right">₹${(customTotal || 0).toLocaleString('en-IN')}</td></tr></tfoot>
        </table>
        <script>
            ${logoUrl ? `var img=document.querySelector('img');if(img&&!img.complete){img.onload=function(){window.print();};img.onerror=function(){window.print()};}else{window.print();}` : `window.print();`}
        </script>
    </body></html>`);
    win.document.close();
  };

  downloadVendorWiseExcel = (vendorRows) => {
    const excelRows = [];
    vendorRows.forEach(r => {
      Object.entries(r.heads).forEach(([head, v]) => {
        excelRows.push([r.vendor_name, r.contact_person, r.contact_no, r.comments, head, v.count, v.total]);
      });
    });

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([["Vendor", "Contact Person", "Contact No", "Comments", "Expense Head", "Transactions", "Amount (₹)"], ...excelRows]);
    XLSX.utils.book_append_sheet(wb, ws, "Vendor Wise");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const fileSuffix = this.state.dateMode === "overall" ? "overall" : `${this.state.fromDate}_to_${this.state.toDate}`;
    saveAs(new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), `vendor_wise_expenses_${fileSuffix}.xlsx`);
  };

  downloadDateWiseExcel = (dateRows) => {
    const excelRows = [];
    dateRows.forEach(r => {
      Object.entries(r.heads).forEach(([head, v]) => {
        excelRows.push([r.date, head, v.count, v.total]);
      });
    });

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([["Date", "Expense Head", "Transactions", "Amount (₹)"], ...excelRows]);
    XLSX.utils.book_append_sheet(wb, ws, "Date Wise");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const fileSuffix = this.state.dateMode === "overall" ? "overall" : `${this.state.fromDate}_to_${this.state.toDate}`;
    saveAs(new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), `date_wise_expenses_${fileSuffix}.xlsx`);
  };

  downloadIndividualExcel = (rowsData) => {
    const excelRows = rowsData.map(r => [
      r.date, r.reference_no, r.head_name, r.category_name, r.vendor_name,
      r.amount, r.tax_amount, r.discount_amount, r.final_amount, r.payment_mode, r.payment_status
    ]);

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([
      ["Date", "Reference No", "Expense Head", "Expense Category", "Vendor", "Amount (₹)", "Tax (₹)", "Discount (₹)", "Final Amount (₹)", "Payment Mode", "Status"],
      ...excelRows
    ]);
    XLSX.utils.book_append_sheet(wb, ws, "Individual Expenses");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const fileSuffix = this.state.dateMode === "overall" ? "overall" : `${this.state.fromDate}_to_${this.state.toDate}`;
    saveAs(new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), `individual_expenses_${fileSuffix}.xlsx`);
  };

  // ── Helper formatters ──────────────────────────────────────────────────────
  formatDate = (val) => {
    if (!val) return "N/A";
    const num = Number(val);
    const d = isNaN(num) ? new Date(val) : new Date(num);
    return d instanceof Date && !isNaN(d) ? d.toLocaleDateString("en-GB", { day: '2-digit', month: 'short', year: 'numeric' }) : "N/A";
  };



  render() {
    const { configs } = this.props;
    const {
      expenses,
      branches,
      branchSearch,
      headSearch,
      categorySearch,
      vendorSearch,
      statusSearch,
      fromDate,
      toDate,
      loading,
      activeTab,
      expandedVendor,
    } = this.state;

    // Get collections from props configs
    const expenseHeads = configs?.getExpenseHeads || [];
    const expenseCategories = configs?.getExpenseCategories || [];
    const expenseVendors = configs?.getExpenseVendors || [];
    const paymentModes = configs?.getPaymentModes || [];
    const isApprover = configs?.getExpenseUserPermission?.isApprover || false;

    // Build filter options
    const headOptions = expenseHeads.map(h => ({ value: h.id, label: h.expense_head }));
    const categoryOptions = expenseCategories.map(c => ({ value: c.id, label: c.expense_categories }));
    const vendorOptions = [
      ...expenseVendors.map(v => ({ value: v.id, label: v.vendor_name })),
      { value: null, label: "Direct" }
    ];
    const statusOptions = [
      { value: "Paid", label: "Approved" },
      { value: "pending", label: "Pending" },
      { value: "Rejected", label: "Rejected" }
    ];

    // Serial-number column reused across every dashboard table
    const snoColumn = {
      dataField: "__sno",
      text: "#",
      isDummyField: true,
      sort: false,
      align: "center",
      headerAlign: "center",
      headerStyle: { width: "60px" },
      formatter: (cell, row, rowIndex) => rowIndex + 1,
    };

    const consolidatedColumns = [
      snoColumn,
      {
        dataField: "head_name",
        text: "Category",
        sort: true,
        align: "center",
        headerAlign: "center",
      },
      {
        dataField: "category_name",
        text: "Sub Category",
        sort: true,
        align: "center",
        headerAlign: "center",
      },
      {
        dataField: "count",
        text: "Txns",
        sort: true,
        align: "center",
        headerAlign: "center",
      },
      {
        dataField: "Paid",
        text: "Approved Amount",
        sort: true,
        align: "center",
        headerAlign: "center",
        formatter: (cell) => rupeeFmt(cell),
      },
      {
        dataField: "pending",
        text: "Pending Amount",
        sort: true,
        align: "center",
        headerAlign: "center",
        formatter: (cell) => rupeeFmt(cell),
      },
      {
        dataField: "Rejected",
        text: "Rejected Amount",
        sort: true,
        align: "center",
        headerAlign: "center",
        formatter: (cell) => rupeeFmt(cell),
      },
      {
        dataField: "total",
        text: "Total Amount",
        sort: true,
        align: "center",
        headerAlign: "center",
        formatter: (cell) => rupeeFmt(cell),
      }
    ];

    const dateWiseColumns = [
      snoColumn,
      {
        dataField: "date",
        text: "Date",
        sort: true,
        align: "center",
        headerAlign: "center",
      },
      {
        dataField: "head_name",
        text: "Category",
        sort: true,
        align: "center",
        headerAlign: "center",
      },
      {
        dataField: "count",
        text: "Txns",
        sort: true,
        align: "center",
        headerAlign: "center",
      },
      {
        dataField: "total",
        text: "Amount",
        sort: true,
        align: "center",
        headerAlign: "center",
        formatter: (cell) => rupeeFmt(cell),
      }
    ];

    const vendorWiseColumns = [
      snoColumn,
      {
        dataField: "vendor_name",
        text: "Vendor Details",
        sort: true,
        formatter: (cell, row) => (
          <div style={{ background: "#f8fafc", color: "#475569", borderRadius: 8, padding: "8px 12px", borderLeft: "4px solid #6366f1" }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#1f2937" }}>{row.vendor_name}</div>
            {(row.contact_person || row.contact_no) && (
              <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4, fontWeight: 500 }}>
                <i className="fal fa-user mr-1" /> {row.contact_person || "—"} ({row.contact_no || "—"})
              </div>
            )}
            {row.comments && (
              <div style={{ fontSize: 11, color: "#6b7280", fontStyle: "italic", marginTop: 2, fontWeight: 400 }}>
                <i className="fal fa-comment-alt mr-1" /> {row.comments}
              </div>
            )}
          </div>
        )
      },
      {
        dataField: "head_name",
        text: "Category",
        sort: true,
      },
      {
        dataField: "count",
        text: "Txns",
        sort: true,
        align: "center",
      },
      {
        dataField: "total",
        text: "Amount",
        sort: true,
        align: "right",
        formatter: (cell) => rupeeFmt(cell),
      }
    ];

    const individualColumns = [
      snoColumn,
      {
        dataField: "date",
        text: "Date",
        sort: true,
      },
      {
        dataField: "reference_no",
        text: "Ref No",
        sort: true,
        formatter: (cell) => (
          <span style={{ background: "#f1f5f9", color: "#475569", borderRadius: 6, padding: "2px 8px", fontSize: 11, fontWeight: 600 }}>{cell || '—'}</span>
        )
      },
      {
        dataField: "head_name",
        text: "Category",
        sort: true,
      },
      {
        dataField: "category_name",
        text: "Sub Category",
        sort: true,
      },
      {
        dataField: "vendor_name",
        text: "Vendor",
        sort: true,
        formatter: (cell) => cell || "Direct"
      },
      {
        dataField: "amount",
        text: "Amount",
        sort: true,
        formatter: (cell) => rupeeFmt(cell),
      },
      {
        dataField: "tax_amount",
        text: "Tax",
        sort: true,
        formatter: (cell) => rupeeFmt(cell),
      },
      {
        dataField: "discount_amount",
        text: "Discount",
        sort: true,
        formatter: (cell) => rupeeFmt(cell),
      },
      {
        dataField: "final_amount",
        text: "Final",
        sort: true,
        formatter: (cell) => rupeeFmt(cell),
      },
      {
        dataField: "payment_mode",
        text: "Payment Mode",
        sort: true,
        formatter: (cell) => (
          <span style={{ background: "#eff6ff", color: "#2563eb", borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 600 }}>{cell}</span>
        )
      },
      {
        dataField: "payment_status",
        text: "Status",
        sort: true,
        formatter: (cell) => (
          <span className={`badge badge-pill ${cell === 'Paid' ? 'badge-success' : cell === 'pending' ? 'badge-warning' : 'badge-danger'}`}>
            {cell === 'Paid' ? 'Approved' : cell}
          </span>
        )
      },
      {
        dataField: "approved_by_name",
        text: "Approver Log",
        sort: true,
        formatter: (cell, row) => {
          if (row.payment_status === "pending") {
            return (
              <span style={{ fontSize: "11px", color: "#f59e0b", fontWeight: 600 }}>
                <i className="fal fa-clock mr-1" /> Pending
              </span>
            );
          }
          return (
            <div style={{ fontSize: "11px", color: "#9ca3af", lineHeight: "1.3" }}>
              <div>{row.payment_status === "Paid" ? "Approved by:" : "Rejected by:"}</div>
              <div className="font-weight-bold" style={{ color: "#374151" }}>{cell || "Admin"}</div>
            </div>
          );
        }
      }
    ];

    // Filter loaded data on the client side for branches, heads, categories, vendors, and status
    const filteredExpenses = expenses.map(e => {
      const pMode = paymentModes.find(m => Number(m.id) === Number(e.payment_mode_id))?.payment_mode || "N/A";
      const dateStr = this.formatDate(e.expense_date);
      return { ...e, date: dateStr, payment_mode: pMode };
    }).filter(exp => {
      // 1. Head filter — empty selection = nothing passes
      if (headSearch.length === 0 || !headSearch.map(Number).includes(Number(exp.expense_head_id))) return false;
      // 2. Category filter — empty selection = nothing passes
      if (categorySearch.length === 0 || !categorySearch.map(Number).includes(Number(exp.expense_category_id))) return false;
      // 3. Status filter — empty selection = nothing passes
      if (statusSearch.length === 0 || !statusSearch.includes(exp.payment_status)) return false;
      // 4. Branch filter (optional field — empty = no restriction)
      if (branchSearch.length > 0 && exp.branch_id && !branchSearch.map(Number).includes(Number(exp.branch_id))) return false;
      // 5. Vendor filter (empty selection = nothing passes; null entry = "Direct" / no vendor)
      if (vendorSearch.length === 0) return false;
      if (exp.expense_vendor_id) {
        const vendorIds = vendorSearch.filter(v => v !== null).map(Number);
        if (!vendorIds.includes(Number(exp.expense_vendor_id))) return false;
      } else {
        if (!vendorSearch.includes(null)) return false;
      }
      return true;
    });

    // Calculations for KPI Cards
    const totalExpenditure = filteredExpenses.reduce((s, e) => s + (e.final_amount || 0), 0);
    const approvedExpenses = filteredExpenses.filter(e => e.payment_status === "Paid");
    const approvedTotal = approvedExpenses.reduce((s, e) => s + (e.final_amount || 0), 0);
    const approvedPct = totalExpenditure ? Math.round((approvedTotal / totalExpenditure) * 100) : 0;

    const pendingExpenses = filteredExpenses.filter(e => e.payment_status === "pending");
    const pendingTotal = pendingExpenses.reduce((s, e) => s + (e.final_amount || 0), 0);
    const pendingCount = pendingExpenses.length;

    const rejectedExpenses = filteredExpenses.filter(e => e.payment_status === "Rejected");
    const rejectedTotal = rejectedExpenses.reduce((s, e) => s + (e.final_amount || 0), 0);
    const rejectedCount = rejectedExpenses.length;

    // Gradients and visual config cards
    const statCards = [
      { label: "Total  Expense", value: totalExpenditure, color: "#6366f1", icon: "fal fa-coins", sub: `${filteredExpenses.length} transactions` },
      { label: "Approved Expenses", value: approvedTotal, color: "#10b981", icon: "fal fa-check-double", sub: `${approvedPct}% approved efficiency` },
      { label: "Pending Approval", value: pendingTotal, color: "#f59e0b", icon: "fal fa-clock", sub: `${pendingCount} items waiting` },
      { label: "Rejected Volume", value: rejectedTotal, color: "#ef4444", icon: "fal fa-times-circle", sub: `${rejectedCount} transactions rejected` },
    ];

    // Chart Data 1: Category Distribution (parent - head_name)
    const parentPieMap = {};
    filteredExpenses.forEach(exp => {
      const hName = exp.head_name || "Uncategorized";
      if (!parentPieMap[hName]) parentPieMap[hName] = 0;
      parentPieMap[hName] += exp.final_amount || 0;
    });
    const parentPieData = Object.entries(parentPieMap).map(([name, value]) => ({ name, value }));

    // Chart Data 1b: Sub-Category Distribution (child - category_name)
    const subCategoryPieMap = {};
    filteredExpenses.forEach(exp => {
      const cName = exp.category_name || "General";
      if (!subCategoryPieMap[cName]) subCategoryPieMap[cName] = 0;
      subCategoryPieMap[cName] += exp.final_amount || 0;
    });
    const subCategoryPieData = Object.entries(subCategoryPieMap).map(([name, value]) => ({ name, value }));

    // Chart Data 2: Area Chart (Daily collection/expense trend)
    const dailyTrendMap = {};
    filteredExpenses.forEach(exp => {
      if (exp.payment_status === "Paid" && exp.date && exp.date !== "N/A") {
        const dStr = moment(exp.date, "DD MMM YYYY").format("DD MMM");
        if (!dailyTrendMap[dStr]) dailyTrendMap[dStr] = 0;
        dailyTrendMap[dStr] += exp.final_amount || 0;
      }
    });
    // Order daily trend chronologically
    const dailyTrend = Object.entries(dailyTrendMap).map(([date, amount]) => ({ date, amount })).sort((a, b) => moment(a.date, "DD MMM").diff(moment(b.date, "DD MMM")));

    // Chart Data 3: Bar Chart (Payment Mode breakdown)
    const paymentModeWiseMap = {};
    filteredExpenses.forEach(exp => {
      if (!paymentModeWiseMap[exp.payment_mode]) paymentModeWiseMap[exp.payment_mode] = { Paid: 0, Pending: 0 };
      if (exp.payment_status === "Paid") {
        paymentModeWiseMap[exp.payment_mode].Paid += exp.final_amount || 0;
      } else {
        paymentModeWiseMap[exp.payment_mode].Pending += exp.final_amount || 0;
      }
    });
    const paymentModeTrend = Object.entries(paymentModeWiseMap).map(([name, val]) => ({
      name,
      Paid: val.Paid,
      Pending: val.Pending
    }));

    // Styling object
    const S = {
      page: { padding: "4px 0" },
      card: { background: "#fff", borderRadius: 18, boxShadow: "0 2px 16px rgba(0,0,0,0.06)", marginBottom: 22 },
      hdr: { background: "linear-gradient(135deg, rgb(99, 102, 241), rgb(139, 92, 246))", padding: "16px 22px", borderRadius: "18px 18px 0 0", display: "flex", alignItems: "center", gap: 10 },
      hdrTitle: { color: "#fff", fontWeight: 700, fontSize: 16, margin: 0 },
      dot: { width: 8, height: 8, borderRadius: "50%", background: "rgba(255,255,255,0.55)", flexShrink: 0 },
      body: { padding: "20px 22px" },
      label: { fontSize: 12, fontWeight: 600, color: "#6b7280", marginBottom: 5, display: "block" },
      input: { borderRadius: 9, border: "1.5px solid #e5e7eb", padding: "8px 12px", fontSize: 13, color: "#1f2937", background: "#f9fafb", width: "100%" },
      secLabel: { fontSize: 13, fontWeight: 700, color: "#6366f1", marginBottom: 14 },
    };

    const btnBase = { border: "none", borderRadius: 9, padding: "9px 20px", fontWeight: 700, fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 8 };

    return (
      <div style={S.page}>
        {/* DateBlock's trigger ships a fixed 230px width that can exceed its filter
            column and push the dashboard sideways. Make ONLY the trigger box fill its
            column; the calendar popup keeps its own (library) sizing. */}
        <style>{`
          .expense-daterange,
          .expense-daterange .customDaterange,
          .expense-daterange #DateTimeRangeContainerNoMobileMode { width: 260px !important; max-width: 100% !important; }
          .expense-daterange .customDaterange input,
          .expense-daterange #DateTimeRangeContainerNoMobileMode input { width: 100% !important; }
        `}</style>
        {/* ── Filters Panel redone as integrated light block ────────────────── */}
        <div style={{ background: "#f8fafc", borderRadius: 16, border: "1.5px solid #e2e8f0", marginBottom: 24, padding: "20px 24px" }}>
          <div style={{ padding: 0 }}>
            <Row className="g-3 align-items-end mb-3">
              <Col lg={3} md={4} sm={6} className="ml-auto">
                <div style={{ width: 260, marginLeft: "auto" }}>
                  <label style={S.label}>Date Range</label>
                  <div className="expense-daterange">
                    <DateBlock applyCallback={this.handleDateBlockCallback} initialStart={fromDate} initialEnd={toDate} leftMode={true} />
                  </div>
                </div>
              </Col>
            </Row>
            <Row className="g-3 align-items-end">
              {/* No Branch filter — the header's branch selection scopes this dashboard. */}
              <Col lg={3} md={4} sm={6}>
                <label style={S.label}>Category</label>
                <CheckboxDropdown
                  options={headOptions}
                  value={headSearch}
                  onChange={(val) => this.setState({ headSearch: val })}
                  placeholder="All Heads"
                />
              </Col>
              <Col lg={3} md={4} sm={6}>
                <label style={S.label}>Sub Category</label>
                <CheckboxDropdown
                  options={categoryOptions}
                  value={categorySearch}
                  onChange={(val) => this.setState({ categorySearch: val })}
                  placeholder="All Categories"
                />
              </Col>
              <Col lg={3} md={4} sm={6}>
                <label style={S.label}>Vendor</label>
                <CheckboxDropdown
                  options={vendorOptions}
                  value={vendorSearch}
                  onChange={(val) => this.setState({ vendorSearch: val })}
                  placeholder="All Vendors"
                />
              </Col>
              <Col lg={3} md={4} sm={6}>
                <label style={S.label}>Status</label>
                <CheckboxDropdown
                  options={statusOptions}
                  value={statusSearch}
                  onChange={(val) => this.setState({ statusSearch: val })}
                  placeholder="All Statuses"
                />
              </Col>
            </Row>

          </div>
        </div>

        {loading ? (
          <ExpenseDashboardShimmer />
        ) : (
          <>
            {/* ── Summary KPI Cards ────────────────────────────────────────── */}
            <Row className="g-3 mb-3">
              {statCards.map(({ label, value, color, icon, sub }) => (
                <Col xl={3} md={6} sm={6} key={label}>
                  <div style={{
                    background: "#fff",
                    border: "1px solid #e5e7eb",
                    borderTop: `4px solid ${color}`,
                    borderRadius: 16,
                    padding: "18px 20px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.03)",
                    display: "flex",
                    alignItems: "center",
                    gap: 16
                  }}>
                    <div style={{
                      background: `${color}15`,
                      color: color,
                      borderRadius: 12,
                      width: 50,
                      height: 50,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0
                    }}>
                      <i className={icon} style={{ fontSize: 22 }} />
                    </div>
                    <div>
                      <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 600 }}>{label}</div>
                      <div style={{ fontSize: 21, fontWeight: 800, color: "#1f2937", lineHeight: 1.2 }}>{rupeeFmt(value)}</div>
                      <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>{sub}</div>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>

            {/* ── Charts Row 1: Category Pie & Bar Charts ───────────────────── */}
            <Row className="g-3 mb-3">
              <Col xl={4} lg={5} md={12}>
                <div style={{ ...S.card, marginBottom: 0, height: "100%" }}>
                  <div style={{ padding: "16px 20px 4px" }}>
                    <div style={S.secLabel}>Category Distribution</div>
                  </div>
                  <div style={{ padding: "0 16px 18px" }}>
                    {parentPieData.length === 0 ? (
                      <div className="text-center py-5 text-muted">No charts data</div>
                    ) : (
                      <ResponsiveContainer width="100%" height={240}>
                        <PieChart>
                          <Pie data={parentPieData} cx="50%" cy="50%" innerRadius={60} outerRadius={90} dataKey="value" paddingAngle={3}>
                            {parentPieData.map((_, i) => <Cell key={i} fill={EXPENSE_COLORS[i % EXPENSE_COLORS.length]} />)}
                          </Pie>
                          <Tooltip content={<PieTip />} />
                          <Legend iconType="circle" iconSize={9} wrapperStyle={{ fontSize: 11 }} />
                        </PieChart>
                      </ResponsiveContainer>
                    )}
                    <div style={{ display: "flex", justifyContent: "center", gap: 16, marginTop: 4 }}>
                      <div style={{ textAlign: "center", background: "#f8fafc", borderRadius: 10, padding: "8px 16px", border: "1px solid #e5e7eb" }}>
                        <div style={{ fontSize: 12, color: "#9ca3af", fontWeight: 600 }}>Approved Ratio</div>
                        <div style={{ fontSize: 20, fontWeight: 800, color: "#10b981" }}>{approvedPct}%</div>
                      </div>
                      <div style={{ textAlign: "center", background: "#f8fafc", borderRadius: 10, padding: "8px 16px", border: "1px solid #e5e7eb" }}>
                        <div style={{ fontSize: 12, color: "#9ca3af", fontWeight: 600 }}>Pending Ratio</div>
                        <div style={{ fontSize: 20, fontWeight: 800, color: "#f97316" }}>
                          {totalExpenditure ? Math.round((pendingTotal / totalExpenditure) * 100) : 0}%
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Col>
              <Col xl={8} lg={7} md={12}>
                <div style={{ ...S.card, marginBottom: 0, height: "100%" }}>
                  <div style={{ padding: "16px 20px 4px" }}>
                    <div style={S.secLabel}>Payment Mode breakdown</div>
                  </div>
                  <div style={{ padding: "0 16px 18px" }}>
                    {paymentModeTrend.length === 0 ? (
                      <div className="text-center py-5 text-muted">No charts data</div>
                    ) : (
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={paymentModeTrend} margin={{ top: 4, right: 16, left: 8, bottom: 16 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                          <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                          <YAxis tickFormatter={(v) => `₹${Math.round(v / 1000)}k`} tick={{ fontSize: 10 }} />
                          <Tooltip content={<BarTip />} />
                          <Legend wrapperStyle={{ fontSize: 12 }} />
                          <Bar dataKey="Paid" name="Approved" fill="#10b981" radius={[5, 5, 0, 0]} maxBarSize={28} />
                          <Bar dataKey="Pending" name="Pending" fill="#f97316" radius={[5, 5, 0, 0]} maxBarSize={28} />
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                </div>
              </Col>
            </Row>

            {/* ── Charts Row 2: Sub-Category Pie & Daily Trend ──────────────── */}
            <Row className="g-3 mb-3">
              <Col xl={4} lg={5} md={12}>
                <div style={{ ...S.card, marginBottom: 0, height: "100%" }}>
                  <div style={{ padding: "16px 20px 4px" }}>
                    <div style={S.secLabel}>Sub-Category Distribution</div>
                  </div>
                  <div style={{ padding: "0 16px 18px" }}>
                    {subCategoryPieData.length === 0 ? (
                      <div className="text-center py-5 text-muted">No charts data</div>
                    ) : (
                      <ResponsiveContainer width="100%" height={240}>
                        <PieChart>
                          <Pie data={subCategoryPieData} cx="50%" cy="50%" innerRadius={60} outerRadius={90} dataKey="value" paddingAngle={3}>
                            {subCategoryPieData.map((_, i) => <Cell key={i} fill={EXPENSE_COLORS[(i + 2) % EXPENSE_COLORS.length]} />)}
                          </Pie>
                          <Tooltip content={<PieTip />} />
                          <Legend iconType="circle" iconSize={9} wrapperStyle={{ fontSize: 11 }} />
                        </PieChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                </div>
              </Col>
              <Col xl={8} lg={7} md={12}>
                <div style={{ ...S.card, marginBottom: 0, height: "100%" }}>
                  <div style={{ padding: "16px 20px 4px" }}>
                    <div style={S.secLabel}>Daily Expense Trend (Approved)</div>
                  </div>
                  <div style={{ padding: "0 16px 18px" }}>
                    {dailyTrend.length === 0 ? (
                      <div className="text-center py-5 text-muted">No trend data for this period</div>
                    ) : (
                      <ResponsiveContainer width="100%" height={240}>
                        <AreaChart data={dailyTrend} margin={{ top: 4, right: 16, left: 8, bottom: 8 }}>
                          <defs>
                            <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#6366f1" stopOpacity={0.25} />
                              <stop offset="95%" stopColor="#6366f1" stopOpacity={0.02} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                          <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                          <YAxis tickFormatter={(v) => `₹${Math.round(v / 1000)}k`} tick={{ fontSize: 10 }} />
                          <Tooltip formatter={(v) => rupeeFmt(v)} labelStyle={{ fontWeight: 700 }} />
                          <Area type="monotone" dataKey="amount" name="Amount" stroke="#6366f1" strokeWidth={2.5} fillOpacity={1} fill="url(#areaGrad)" dot={{ fill: "#6366f1", r: 4 }} />
                        </AreaChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                </div>
              </Col>
            </Row>

            {/* ── Multi-View Report Tabs & Tables ───────────────────────────── */}
            <div style={{ marginTop: 32 }}>
              {/* Clean typography section header */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 0 }}>
                <h5 style={{ margin: 0, fontWeight: 800, color: "#1f2937", fontSize: 16, display: "flex", alignItems: "center", gap: 8 }}>
                  <i className="fal fa-chart-line" style={{ color: "#6366f1" }} />
                  Operational Reports
                </h5>
                <span style={{ background: "#eff6ff", color: "#6366f1", borderRadius: 20, padding: "4px 14px", fontSize: 12, fontWeight: 700, border: "1px solid #c7d2fe" }}>
                  {filteredExpenses.length} Records Found
                </span>
              </div>
              {/* Sub-header tab strip matching the top ExpenseManagementMain nav style */}
              <div style={{ background: "#f8fafc", borderBottom: "1.5px solid #e2e8f0", padding: "12px 0 0 0", marginTop: 16 }}>
                <ul className="nav nav-tabs" style={{ borderBottom: "none", gap: 8 }}>
                  {[
                    { key: "consolidated", label: "Consolidated", icon: "fal fa-chart-pie" },
                    { key: "date_wise", label: "Date Wise Report", icon: "fal fa-calendar-alt" },
                    { key: "vendor_wise", label: "Vendor Wise Report", icon: "fal fa-store" },
                    { key: "individual", label: "Individual Report", icon: "fal fa-receipt" }
                  ].map(({ key, label, icon }) => {
                    const active = activeTab === key;
                    return (
                      <li className="nav-item" key={key}>
                        <a
                          className={`nav-link ${active ? "active" : ""}`}
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            this.setState({ activeTab: key });
                          }}
                          style={{
                            cursor: "pointer",
                            fontWeight: 700,
                            fontSize: 14,
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            padding: "12px 20px",
                            color: active ? "#6366f1" : "#4b5563",
                            background: active ? "#fff" : "transparent",
                            borderColor: active ? "#e2e8f0 #e2e8f0 #fff" : "transparent",
                            borderWidth: "1.5px",
                            borderBottomWidth: active ? "2px" : "1.5px",
                            borderRadius: "10px 10px 0 0",
                            transition: "all 0.15s ease",
                            marginBottom: "-1.5px"
                          }}
                        >
                          <i className={icon} /> {label}
                        </a>
                      </li>
                    );
                  })}
                </ul>
              </div>
              <div style={{ padding: "20px 0" }}>

                {/* Tab Views */}
                {activeTab === "consolidated" ? (
                  /* ── Consolidated View ── */
                  (() => {
                    const grpMap = {};
                    filteredExpenses.forEach(e => {
                      const key = `${e.head_name}||${e.category_name}`;
                      if (!grpMap[key]) {
                        grpMap[key] = {
                          head_name: e.head_name,
                          category_name: e.category_name,
                          count: 0,
                          total: 0,
                          Paid: 0,
                          pending: 0,
                          Rejected: 0
                        };
                      }
                      grpMap[key].count++;
                      grpMap[key].total += e.final_amount || 0;
                      if (e.payment_status === "Paid") grpMap[key].Paid += e.final_amount || 0;
                      else if (e.payment_status === "pending") grpMap[key].pending += e.final_amount || 0;
                      else if (e.payment_status === "Rejected") grpMap[key].Rejected += e.final_amount || 0;
                    });
                    const rows = Object.values(grpMap).map((r, i) => ({ ...r, id: i + 1 }));

                    return (
                      <CardLessDataTable
                        parentData={rows}
                        particlecolumns={consolidatedColumns}
                        name="consolidated_expenses"
                        searchBar={true}
                        placeholder="Search Consolidated Expenses..."
                      />
                    );
                  })()
                ) : activeTab === "date_wise" ? (
                  /* ── Date Wise View ── */
                  (() => {
                    const dateMap = {};
                    let grandTotal = 0;
                    let txnTotal = 0;

                    filteredExpenses.forEach(e => {
                      const d = e.date || "N/A";
                      if (!dateMap[d]) dateMap[d] = { date: d, heads: {}, count: 0, total: 0 };
                      dateMap[d].count++;
                      dateMap[d].total += e.final_amount || 0;
                      grandTotal += e.final_amount || 0;
                      txnTotal++;

                      const h = e.head_name || "Uncategorized";
                      if (!dateMap[d].heads[h]) dateMap[d].heads[h] = { count: 0, total: 0 };
                      dateMap[d].heads[h].count++;
                      dateMap[d].heads[h].total += e.final_amount || 0;
                    });
                    const dateRows = [];
                    let dIdx = 1;
                    Object.values(dateMap).sort((a, b) => moment(a.date, "DD MMM YYYY").diff(moment(b.date, "DD MMM YYYY"))).forEach(r => {
                      Object.entries(r.heads).forEach(([head, v]) => {
                        dateRows.push({
                          id: dIdx++,
                          date: r.date,
                          head_name: head,
                          count: v.count,
                          total: v.total
                        });
                      });
                    });

                    return (
                      <>
                        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12, gap: 8 }}>
                          <button onClick={() => this.printDateWiseReport(Object.values(dateMap).sort((a, b) => moment(a.date, "DD MMM YYYY").diff(moment(b.date, "DD MMM YYYY"))), grandTotal, txnTotal)} style={{ ...btnBase, background: "linear-gradient(135deg, #10b981 0%, #059669 100%)", color: "#fff" }}>
                            <i className="fal fa-print" /> Print / PDF
                          </button>
                          <button onClick={() => this.downloadDateWiseExcel(Object.values(dateMap).sort((a, b) => moment(a.date, "DD MMM YYYY").diff(moment(b.date, "DD MMM YYYY"))))} style={{ ...btnBase, background: "linear-gradient(135deg, #10b981 0%, #059669 100%)", color: "#fff" }}>
                            <i className="fal fa-file-excel" /> Download Excel
                          </button>
                        </div>
                        <CardLessDataTable
                          parentData={dateRows}
                          particlecolumns={dateWiseColumns}
                          name="date_wise_expenses"
                          searchBar={false}
                        />
                      </>
                    );
                  })()
                ) : activeTab === "vendor_wise" ? (
                  /* ── Vendor Wise View ── */
                  (() => {
                    const vendorMap = {};
                    let grandTotal = 0;
                    let txnTotal = 0;

                    filteredExpenses.forEach(e => {
                      const vName = e.vendor_name || "Direct / No Vendor";
                      if (!vendorMap[vName]) {
                        const fullVendor = expenseVendors.find(v => v.vendor_name === e.vendor_name) || {};
                        vendorMap[vName] = {
                          vendor_name: vName,
                          contact_person: fullVendor.contact_person || "",
                          contact_no: fullVendor.contact_no || "",
                          comments: fullVendor.comments || "",
                          heads: {},
                          count: 0,
                          total: 0
                        };
                      }
                      vendorMap[vName].count++;
                      vendorMap[vName].total += e.final_amount || 0;
                      grandTotal += e.final_amount || 0;
                      txnTotal++;

                      const h = e.head_name || "Uncategorized";
                      if (!vendorMap[vName].heads[h]) {
                        vendorMap[vName].heads[h] = { count: 0, total: 0 };
                      }
                      vendorMap[vName].heads[h].count++;
                      vendorMap[vName].heads[h].total += e.final_amount || 0;
                    });

                    const vendorRows = [];
                    let vIdx = 1;
                    Object.values(vendorMap).sort((a, b) => a.vendor_name.localeCompare(b.vendor_name)).forEach(r => {
                      Object.entries(r.heads).forEach(([head, v]) => {
                        vendorRows.push({
                          id: vIdx++,
                          vendor_name: r.vendor_name,
                          contact_person: r.contact_person,
                          contact_no: r.contact_no,
                          comments: r.comments,
                          head_name: head,
                          count: v.count,
                          total: v.total
                        });
                      });
                    });

                    return (
                      <>
                        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12, gap: 8 }}>
                          <button onClick={() => this.printVendorWiseReport(Object.values(vendorMap).sort((a, b) => a.vendor_name.localeCompare(b.vendor_name)), grandTotal, txnTotal)} style={{ ...btnBase, background: "linear-gradient(135deg, #10b981 0%, #059669 100%)", color: "#fff" }}>
                            <i className="fal fa-print" /> Print / PDF
                          </button>
                          <button onClick={() => this.downloadVendorWiseExcel(Object.values(vendorMap).sort((a, b) => a.vendor_name.localeCompare(b.vendor_name)))} style={{ ...btnBase, background: "linear-gradient(135deg, #10b981 0%, #059669 100%)", color: "#fff" }}>
                            <i className="fal fa-file-excel" /> Download Excel
                          </button>
                        </div>
                        <CardLessDataTable
                          parentData={vendorRows}
                          particlecolumns={vendorWiseColumns}
                          name="vendor_wise_expenses"
                          searchBar={true}
                          placeholder="Search Vendor Wise Expenses..."
                        />
                      </>
                    );
                  })()
                ) : (
                  /* ── Individual View ── */
                  <>
                    <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12, gap: 8 }}>
                      <button onClick={() => this.printIndividualReport(filteredExpenses, totalExpenditure)} style={{ ...btnBase, background: "linear-gradient(135deg, #10b981 0%, #059669 100%)", color: "#fff" }}>
                        <i className="fal fa-print" /> Print / PDF
                      </button>
                      <button onClick={() => this.downloadIndividualExcel(filteredExpenses)} style={{ ...btnBase, background: "linear-gradient(135deg, #10b981 0%, #059669 100%)", color: "#fff" }}>
                        <i className="fal fa-file-excel" /> Download Excel
                      </button>
                    </div>
                    <CardLessDataTable
                      parentData={filteredExpenses}
                      particlecolumns={individualColumns}
                      name="individual_expenses"
                      searchBar={true}
                      placeholder="Search Individual Expenses..."
                    />
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    );
  }
}

export default ExpenseDashboard;
